import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { fireEpicenterGtm } from "../../datalayers";
import styles from "./style.module.css";
import bellIcon from "./bell.png";
import alarmSound from "./no_show_alarm.mp3";

/**
 * Pre-class attendance alarm popup (no-show prevention).
 *
 * Rendered when the native app forwards a `showClassAlarm` message. The popup
 * is intentionally NON-DISMISSIBLE: there is no close button, clicking the
 * backdrop does nothing, and the Escape key / Android hardware back are
 * intercepted. The mentor must confirm by tapping the single action button.
 *
 * The popup OWNS the alarm sound: it loops `no_show_alarm.mp3` until the mentor
 * taps the button or ALARM_WINDOW_MS (90s) elapses. On tap it posts
 * `alarmResponse` back to the native layer, shows a brief confirmation, and
 * closes.
 */

const ALARM_WINDOW_MS = 90 * 1000;
const SUCCESS_AUTO_CLOSE_MS = 2500;

function formatTiming(startTimeMs) {
  if (!startTimeMs) return "";
  try {
    const d = new Date(Number(startTimeMs));
    const day = d.getDate();
    const month = d.toLocaleString("en-US", { month: "long" });
    const year = d.getFullYear();
    const time = d.toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${day} ${month} ${year}, ${time} IST`;
  } catch (e) {
    return "";
  }
}

// mentorId is still passed by the caller but no longer read: both no-show
// events now label themselves with the mentor's email via fireEpicenterGtm.
export default function ClassAlarmModal({ payload, onClose }) {
  const [responded, setResponded] = useState(false);
  const audioRef = useRef(null);
  const stopTimerRef = useRef(null);

  const shownAtRef = useRef(null);

  const stopSound = () => {
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    const audio = audioRef.current;
    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (e) {
        /* ignore */
      }
    }
  };

  const startSound = () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.loop = true;
      audio.volume = 1;
      const p = audio.play();
      if (p && typeof p.catch === "function") {
        // Autoplay may be blocked; native notification sound already alerted
        // the mentor, so this is best-effort.
        p.catch(() => { });
      }
    } catch (e) {
      /* audio not available */
    }
  };

  useEffect(() => {
    startSound();
    shownAtRef.current = Date.now();

    // Via the shared helper: the old dataLayer + initialize pair pushed the
    // payload twice and re-loaded the container on every alarm. event_label is
    // now the mentor's email, as on every other Epicenter event, rather than
    // the mentor id this pair used to send.
    fireEpicenterGtm("noshow_noti_shown", [
      "mentor_noshow",
      "notification_screen",
      payload?.scheduleId,
    ]);
    // Stop the alarm after the 90s window even if untouched; keep popup visible.
    stopTimerRef.current = setTimeout(stopSound, ALARM_WINDOW_MS);

    // Intercept Android hardware back / browser back while the alarm is open.
    const blockBack = () => {
      window.history.pushState(null, "", window.location.href);
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", blockBack);

    // Intercept Escape.
    const blockEsc = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", blockEsc, true);

    return () => {
      stopSound();
      window.removeEventListener("popstate", blockBack);
      window.removeEventListener("keydown", blockEsc, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJoin = () => {
    stopSound();
    setResponded(true);
    // Analytics must never be able to stop the alarm from being answered, so
    // this is isolated from the postMessage below rather than sharing its try.
    try {
      fireEpicenterGtm("noshow_noti_clicked", [
        "mentor_noshow",
        "notification_screen",
        payload?.scheduleId,
        shownAtRef.current
          ? Math.round((Date.now() - shownAtRef.current) / 1000)
          : "",
      ]);
    } catch (e) {
      /* ignore */
    }
    try {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: "alarmResponse",
            response: "will_join",
            scheduleId: payload?.scheduleId,
          })
        );
      }
    } catch (e) {
      /* ignore */
    }
    // Show the confirmation toast briefly, then close.
    setTimeout(() => {
      if (onClose) onClose();
    }, SUCCESS_AUTO_CLOSE_MS);
  };

  const timing = formatTiming(payload?.startTimeMs);

  const modal = (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <audio ref={audioRef} src={alarmSound} preload="auto" />
      {responded ? (
        <div className={styles.toast}>
          <span className={styles.toastText}>
            Thanks for confirming your attendance
          </span>
        </div>
      ) : (
        <div className={styles.card}>
          <div className={styles.header}>
            <img className={styles.bell} src={bellIcon} alt="" aria-hidden="true" />
            <p className={styles.title}>
              Your class has already started. Please join the classroom
              immediately.
            </p>
          </div>

          <div className={styles.details}>
            {payload?.studentName ? (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Student</span>
                <span className={styles.detailValue}>{payload.studentName}</span>
              </div>
            ) : null}
            {payload?.courseName ? (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Course</span>
                <span className={styles.detailValue}>{payload.courseName}</span>
              </div>
            ) : null}
            {timing ? (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Timing</span>
                <span className={styles.detailValue}>{timing}</span>
              </div>
            ) : null}
          </div>

          <button
            type="button"
            className={styles.primaryBtn}
            onClick={handleJoin}
          >
            Yes, I'll join now
          </button>
        </div>
      )}
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
